import { useEffect, useMemo, useState } from "react";
import { styles } from "../../styles";
import { DAYS } from "../../lib/constants";
import { timeOptions } from "../../lib/time";
import { TimeSelect, ErrorNote } from "../common";
import { useShop } from "../../context/ShopContext";
import { useToast } from "../../context/ToastContext";
import { saveShopConfig } from "../../data/config";
import { logAudit } from "../../data/audit";
import type { Hours } from "../../lib/types";

export function HoursCard() {
  const { config } = useShop();
  const notify = useToast();
  const [draft, setDraft] = useState<Hours>(config.hours);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const opts = useMemo(() => timeOptions(), []);

  // Re-sync when config changes (for example, another device saved).
  useEffect(() => setDraft(config.hours), [config.hours]);

  const setDay = (i: number, patch: Partial<Hours[number]>) =>
    setDraft((h) => ({ ...h, [i]: { ...h[i], ...patch } }));

  const dirty = JSON.stringify(draft) !== JSON.stringify(config.hours);

  const invalid = Object.values(draft).some((d) => d.open && d.end <= d.start);

  const save = async () => {
    setError(null);
    if (invalid) {
      setError("Each open day needs a closing time later than its opening time.");
      return;
    }
    setBusy(true);
    try {
      await saveShopConfig({ hours: draft });
      void logAudit("Updated days and hours");
      notify("Hours saved.");
    } catch {
      setError("We could not save your hours. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.card}>
      <h2 style={styles.cardTitle}>Days &amp; hours</h2>
      <p style={styles.sub}>Toggle the days you're open and set opening times.</p>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div style={styles.hoursList}>
        {DAYS.map((name, i) => {
          const cfg = draft[i];
          return (
            <div key={i} style={styles.hourRow}>
              <label style={styles.toggleWrap}>
                <input
                  type="checkbox" checked={cfg.open}
                  onChange={(e) => setDay(i, { open: e.target.checked })}
                  className="sr-check" aria-label={`${name} open`}
                />
                <span className="switch" style={{ ...styles.switch, ...(cfg.open ? styles.switchOn : {}) }}>
                  <span style={{ ...styles.knob, ...(cfg.open ? styles.knobOn : {}) }} />
                </span>
                <span style={styles.dayName}>{name}</span>
              </label>
              <div style={{ ...styles.hourTimes, opacity: cfg.open ? 1 : 0.35, pointerEvents: cfg.open ? "auto" : "none" }}>
                <TimeSelect value={cfg.start} onChange={(v) => setDay(i, { start: v })} opts={opts} label={`${name} opening time`} />
                <span style={styles.toDash}>to</span>
                <TimeSelect value={cfg.end} onChange={(v) => setDay(i, { end: v })} opts={opts} label={`${name} closing time`} />
              </div>
            </div>
          );
        })}
      </div>
      <div style={styles.msgActions}>
        <button className="primary" style={styles.primarySm} onClick={save} disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save hours"}
        </button>
      </div>
    </div>
  );
}
